import { api } from '../../../plugins/axios';
import { Alert } from '../../../plugins/alert';
import { invoiceTypes } from '../../invoices/entities/invoice';

export default class Shipment {
	id = undefined;
	status = undefined;
	shipping_items = [];
	name = undefined;
	created_at = undefined;
	tracking_number = undefined;
	tracking_url = undefined;
	shipping_fee = undefined;
	marketplace_id = undefined;
	linked_marketplace_id = undefined;
	linked_shipping_company_id = undefined;
	shipment_company_name = undefined;
	invoice_link = undefined;
	main_order_id = undefined;

	constructor(obj) {
		Object.keys(obj).forEach((key) => {
			this[key] = obj[key];
		});
		this.shipping_items = this.shipping_items.map((item) => ({
			...item,
			splitQty: item.shipped_quantity,
		}));
	}

	static async getShippingCompanies(data) {
		return api
			.post('main-order/shipment/check-delivery-fee', data)
			.then((res) => res.data?.result?.delivery_companies || []);
	}

	async split(items) {
		return api.post('/main-order/shipment/split-shipment', { shipment_id: this.id, items });
	}

	async cancelShipment() {
		return api.get(`main-order/shipment/cancel-shipment/${this.id}`);
	}

	async printLabel() {
		const { data } = await api.get(`main-order/shipment/print-label/${this.id}`, {
			responseType: 'arraybuffer',
			headers: {
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			},
		});
		const blob = new Blob([data], { type: 'application/pdf' });
		return blob;
	}

	async shipBySeller(data) {
		return api.post(`main-order/shipment/ship-by-seller/${this.id}`, data);
	}

	async shipBySalesChannel(data) {
		return api.post(`main-order/shipment/ship-by-sales-channel/${this.id}`, data);
	}

	async shipByLinker(data) {
		try {
			return await api.post(`main-order/shipment/ship-by-linker/${this.id}`, data);
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	async updateStatus(status) {
		await api.post(`main-order/shipment/update-status/${this.id}`, { status });
		this.status = status;
	}

	async addInvoiceLink(invoiceLink) {
		try {
			await api.post(`/shipping/invoice/add-link/${this.id}`, { invoice_link: invoiceLink });
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	async uploadInvoice(formData) {
		try {
			await api.post(`/shipping/invoice/upload/${this.id}`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	async sendInvoiceToSalesChannel() {
		await api.get(`/shipping/invoice/send-to-sales-channel/${this.id}`);
	}

	async removeInvoice() {
		await api.get(`/shipping/invoice/remove/${this.id}`);
	}

	async downloadInvoice() {
		const { data } = await api.get(`/shipping/invoice/download/${this.id}`, {
			responseType: 'arraybuffer',
			headers: {
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			},
		});
		const blob = new Blob([data], { type: 'application/pdf' });
		return blob;
	}

	async generateZatcaInvoice() {
		await api.get(`/zatca/generate-invoice/${this.main_order_id}`, {
			params: {
				type: invoiceTypes.SIMPLE,
				shipment_id: this.id,
			},
		});
	}
}

export const shipmentStatusEnum = Object.freeze({
	DRAFT: 'Draft',
	NEW: 'New',
	IN_PREPARATION: 'In Preparation',
	READY_FOR_SHIPPING: 'Ready for shipping',
	SHIPMENT_CREATED: 'Shipment Created',
	SHIPPED: 'Shipped', // picked
	DELIVERED: 'Delivered',
	SHIPMENT_CANCELLED: 'Shipment Cancelled',
	IN_PROGRESS_RETURN: 'in progress return',
	RETURNED: 'Returned',
	RETURN_FAILED: 'Return Failed',
});

export const shipmentStatusBadgeVariant = Object.freeze({
	[shipmentStatusEnum.DRAFT]: 'gray',
	[shipmentStatusEnum.NEW]: 'gray',
	[shipmentStatusEnum.IN_PREPARATION]: 'brand',
	[shipmentStatusEnum.READY_FOR_SHIPPING]: 'brand',
	[shipmentStatusEnum.SHIPMENT_CREATED]: 'brand',
	[shipmentStatusEnum.SHIPPED]: 'blue',
	[shipmentStatusEnum.DELIVERED]: 'success',
	[shipmentStatusEnum.SHIPMENT_CANCELLED]: 'error',
	[shipmentStatusEnum.IN_PROGRESS_RETURN]: 'warning',
	[shipmentStatusEnum.RETURNED]: 'success',
	[shipmentStatusEnum.RETURN_FAILED]: 'error',
});
